import { COLORS, DISABLED_BTN } from "../shared/constants";
import { IconBack, IconAlert, IconEdit, IconTrash, IconPlus } from "../shared/icons";
import { canDo, formatDate, getUserName, localDateStr } from "../shared/utils";
import { useState, useRef, useEffect } from "react";
import * as api from "../api";
import AssessmentEntryForm from "./AssessmentEntryForm";
import AssessmentTrendGraphs from "./AssessmentTrendGraphs";
import AssignTreatmentForm from "./AssignTreatmentForm";
import ReminderForm from "./ReminderForm";
import ReportsSection from "./ReportsSection";
import ScheduleVisitModal from "./ScheduleVisitModal";
import AmendmentModal from "./AmendmentModal";

export default // ─── Patient Detail Screen ───────────────────────────────────
function PatientDetail({ patient, treatments, protocols, diagnoses, coils, treatmentSpots, mtSpots, users, currentUser, reminders, providers, clinicInfo, onNavigate, onEditPatient, onAssignTreatment, onUpdateTreatment, onStartSession, onSaveReminder, onDeleteReminder, sessions, assessmentScales, assessments, onSubmitAssessment, onGenerateLink, onRefreshAssessments, pendingAssessment, onClearPendingAssessment, onAmendSession, onAmendAssessment, onVoidSession, onVoidAssessment }) {
  const [showAssign, setShowAssign] = useState(false);
  const [amendModal, setAmendModal] = useState(null); // { type, record, field, fieldLabel, currentValue }
  const [voidConfirm, setVoidConfirm] = useState(null); // { type: "session"|"assessment", record, reason }
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [endingTreatment, setEndingTreatment] = useState(null);
  const [endReason, setEndReason] = useState("");
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [expandedHistoryTx, setExpandedHistoryTx] = useState({});
  const [showAssessmentForm, setShowAssessmentForm] = useState(null); // { treatmentId, scaleId } or null
  const [expandedAssessments, setExpandedAssessments] = useState({}); // { adminId: true }
  const [showQR, setShowQR] = useState(null); // { link, scaleName } or null
  const [expandedAssessmentTx, setExpandedAssessmentTx] = useState({}); // collapsed treatment groups
  const [trendTreatmentIds, setTrendTreatmentIds] = useState(null); // Set of selected treatment IDs (null = active treatments)
  const [assessmentTab, setAssessmentTab] = useState("scores"); // "scores" | "trends"
  const [refreshingAssessments, setRefreshingAssessments] = useState(false);
  const assessmentSectionRef = useRef(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [calConnected, setCalConnected] = useState(false);
  const [selectedVisits, setSelectedVisits] = useState([]);
  const [deletingVisits, setDeletingVisits] = useState(false);
  const [showManualSession, setShowManualSession] = useState(null); // treatment_id or null
  const [manualSessionForm, setManualSessionForm] = useState({});
  const [savingManual, setSavingManual] = useState(false);
  const [editingSessionCount, setEditingSessionCount] = useState(null); // { txId, completed, planned }

  const handleDeleteSelectedVisits = async () => {
    if (selectedVisits.length === 0) return;
    setDeletingVisits(true);
    for (const eventId of selectedVisits) {
      try {
        await api.calendarDeleteEvent(eventId);
      } catch (err) { console.error("Delete event:", err); }
    }
    setUpcomingVisits((prev) => prev.filter((ev) => !selectedVisits.includes(ev.id)));
    setSelectedVisits([]);
    setDeletingVisits(false);
  };

  // Load upcoming visits for this patient from Google Calendar
  useEffect(() => {
    api.calendarStatus().then((s) => {
      if (s && s.connected) {
        setCalConnected(true);
        const today = localDateStr(new Date());
        api.calendarGetEvents({ date: today, days: 30 }).then((res) => {
          if (res && res.events) {
            const patientName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
            const matched = res.events.filter((ev) => {
              const lower = (ev.summary || "").toLowerCase();
              return lower.includes(patientName) || lower.includes(`${patient.last_name} ${patient.first_name}`.toLowerCase());
            });
            setUpcomingVisits(matched);
          }
        }).catch(() => {});
      }
    }).catch(() => {});
  }, [patient.first_name, patient.last_name]);

  // Handle pendingAssessment from TreatmentSessionScreen reminder click
  useEffect(() => {
    if (pendingAssessment) {
      setShowAssessmentForm(pendingAssessment);
      setTimeout(() => {
        assessmentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      if (onClearPendingAssessment) onClearPendingAssessment();
    }
  }, [pendingAssessment]);

  const activeTx = treatments.filter((t) => t.status === "ACTIVE");
  const pastTx = treatments.filter((t) => t.status !== "ACTIVE");

  const statusBadge = (status) => {
    const cfg = {
      ACTIVE: { bg: "#E8F8F0", color: "#2E9E6A", label: "Active" },
      COMPLETED: { bg: "#ECEDF0", color: "#5A6178", label: "Completed" },
      ENDED_EARLY: { bg: "#FDEDEC", color: "#C0392B", label: "Ended Early" },
    };
    const c = cfg[status] || cfg.ACTIVE;
    return <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "600", background: c.bg, color: c.color }}>{c.label}</span>;
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={() => onNavigate("PATIENT_LIST")} style={{ background: "none", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", color: COLORS.textSecondary }}><IconBack /></button>
          <div>
            <h2 style={{ color: COLORS.text, fontSize: "22px", fontWeight: "600", margin: 0 }}>
              {patient.first_name} {patient.last_name}
              <span style={{ color: COLORS.textSecondary, fontWeight: "400", fontSize: "14px", marginLeft: "10px" }}>#{patient.patient_number}</span>
            </h2>
            <div style={{ fontSize: "13px", color: COLORS.textSecondary, marginTop: "4px" }}>
              DOB: {formatDate(patient.date_of_birth)} · {patient.gender} · {patient.diagnosis_code}
            </div>
          </div>
        </div>
        <button onClick={canDo(currentUser, "create_edit_patients") ? onEditPatient : undefined}
          style={{ height: "40px", padding: "0 18px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.primary, fontSize: "13px", fontWeight: "600", cursor: canDo(currentUser, "create_edit_patients") ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: "6px", ...(canDo(currentUser, "create_edit_patients") ? {} : DISABLED_BTN) }}>
          <IconEdit /> Edit Patient
        </button>
      </div>

      {/* Precautions Banner */}
      {patient.precautions && (
        <div style={{
          background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "10px",
          padding: "12px 16px", marginBottom: "20px", display: "flex", alignItems: "flex-start", gap: "10px",
        }}>
          <span style={{ color: COLORS.danger, flexShrink: 0, marginTop: "1px" }}><IconAlert /></span>
          <div>
            <span style={{ fontSize: "12px", fontWeight: "700", color: COLORS.danger, textTransform: "uppercase", letterSpacing: "0.5px" }}>Precautions</span>
            <p style={{ margin: "4px 0 0", fontSize: "14px", color: COLORS.text, lineHeight: "1.5" }}>{patient.precautions}</p>
          </div>
        </div>
      )}

      {/* Reminders Management */}
      <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, margin: 0 }}>
            Reminders
            <span style={{ color: COLORS.textSecondary, fontWeight: "400", fontSize: "13px", marginLeft: "8px" }}>
              ({(reminders || []).filter((r) => treatments.some((t) => t.id === r.treatment_assignment_id && t.status === "ACTIVE") && r.is_active).length})
            </span>
          </h3>
          <button onClick={canDo(currentUser, "create_edit_reminders") ? () => { setEditingReminder(null); setShowAddReminder(true); } : undefined}
            style={{ height: "32px", padding: "0 14px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "12px", fontWeight: "600", cursor: canDo(currentUser, "create_edit_reminders") ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: "4px", ...(canDo(currentUser, "create_edit_reminders") ? {} : DISABLED_BTN) }}>
            <IconPlus /> Add Reminder
          </button>
        </div>

        {showAddReminder && (
          <ReminderForm
            reminder={editingReminder}
            treatments={treatments.filter((t) => t.status === "ACTIVE")}
            currentUser={currentUser}
            assessmentScales={assessmentScales}
            clinicInfo={clinicInfo}
            onSave={(rem) => { onSaveReminder(rem); setShowAddReminder(false); setEditingReminder(null); }}
            onCancel={() => { setShowAddReminder(false); setEditingReminder(null); }}
          />
        )}

        {(() => {
          const activeTxIds = treatments.filter((t) => t.status === "ACTIVE").map((t) => t.id);
          const activeReminders = (reminders || []).filter((r) => activeTxIds.includes(r.treatment_assignment_id) && r.is_active);
          const colorMap = { yellow: { bg: "#FFF9E6", dot: "#D4A800" }, blue: { bg: "#E8F0FE", dot: "#1A56DB" }, orange: { bg: "#FFF3E6", dot: "#D4700A" } };

          return activeReminders.length === 0 && !showAddReminder ? (
            <div style={{ padding: "20px", textAlign: "center", color: COLORS.textSecondary, fontSize: "13px" }}>No reminders configured. Default reminders (MT, PHQ-9) are created when a treatment is assigned.</div>
          ) : (
            activeReminders.map((rem) => {
              const c = colorMap[rem.color] || colorMap.orange;
              const tx = treatments.find((t) => t.id === rem.treatment_assignment_id);
              const isDueAssessment = rem.status === "DUE" && rem.reminder_type === "ASSESSMENT";
              const isDueMT = rem.status === "DUE" && rem.reminder_type === "MT_REDETERMINATION";
              const isClickableReminder = isDueAssessment || isDueMT;
              const handleReminderRowClick = () => {
                if (isDueAssessment) {
                  const scale = (assessmentScales || []).find((s) => s.short_name === rem.label);
                  setShowAssessmentForm({ treatmentId: rem.treatment_assignment_id, scaleId: scale?.id || null });
                  setTimeout(() => {
                    assessmentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 100);
                } else if (isDueMT && tx) {
                  onStartSession(tx);
                }
              };
              return (
                <div key={rem.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                  <div onClick={handleReminderRowClick}
                    style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, cursor: isClickableReminder ? "pointer" : "default" }}>
                    <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "500", color: COLORS.text }}>
                        {rem.label}
                        {rem.status === "DUE" && <span style={{ marginLeft: "6px", padding: "1px 6px", borderRadius: "4px", fontSize: "10px", fontWeight: "700", background: c.dot, color: "#FFF" }}>DUE</span>}
                      </div>
                      <div style={{ fontSize: "11px", color: COLORS.textSecondary }}>
                        {tx?.protocol_name} · {rem.schedule_type === "SESSION_BASED" ? (rem.session_interval > 0 ? `Every ${rem.session_interval} sessions` : "Not configured") : rem.schedule_type === "PERIODIC" ? `Every ${rem.periodic_value} ${(rem.periodic_unit || "").toLowerCase()}` : "Custom schedule"}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "4px" }}>
                    {rem.status === "DUE" && (
                      <button onClick={() => onSaveReminder({ ...rem, status: "PENDING", last_triggered_at_session: null })}
                        style={{ height: "26px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.accent}`, background: "#E8F8F0", color: COLORS.accent, fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>✓ Clear</button>
                    )}
                    <button onClick={() => { setEditingReminder(rem); setShowAddReminder(true); }}
                      style={{ height: "26px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.primary, fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}><IconEdit /></button>
                    <button onClick={() => onDeleteReminder(rem.id)}
                      style={{ height: "26px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.dangerBorder}`, background: COLORS.dangerLight, color: COLORS.danger, fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}><IconTrash /></button>
                  </div>
                </div>
              );
            })
          );
        })()}
      </div>

      {/* Active Treatments */}
      <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, margin: 0 }}>
            Active Treatments
            <span style={{ color: COLORS.textSecondary, fontWeight: "400", fontSize: "13px", marginLeft: "8px" }}>({activeTx.length})</span>
          </h3>
          <button onClick={canDo(currentUser, "assign_treatment") ? () => setShowAssign(true) : undefined}
            style={{ height: "36px", padding: "0 16px", borderRadius: "8px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "13px", fontWeight: "600", cursor: canDo(currentUser, "assign_treatment") ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: "4px", ...(canDo(currentUser, "assign_treatment") ? {} : DISABLED_BTN) }}>
            <IconPlus /> Assign Treatment
          </button>
        </div>

        {showAssign && (
          <AssignTreatmentForm
            protocols={protocols}
            diagnoses={diagnoses}
            coils={coils}
            treatmentSpots={treatmentSpots}
            mtSpots={mtSpots}
            patient={patient}
            currentUser={currentUser}
            onAssign={(tx) => { onAssignTreatment(tx); setShowAssign(false); }}
            onCancel={() => setShowAssign(false)}
          />
        )}

        {activeTx.length === 0 && !showAssign ? (
          <div style={{ padding: "32px", textAlign: "center", color: COLORS.textSecondary }}>
            <p style={{ fontSize: "14px" }}>No active treatments. Assign a protocol to begin.</p>
          </div>
        ) : (
          activeTx.map((tx) => (
            <div key={tx.id} style={{ padding: "16px", border: `1px solid ${COLORS.borderLight}`, borderRadius: "10px", marginBottom: "10px", background: COLORS.inputBg }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <span style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text }}>{tx.protocol_name}</span>
                    {statusBadge(tx.status)}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, auto)", gap: "20px", fontSize: "13px", color: COLORS.textSecondary }}>
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "2px" }}>Diagnosis</div>
                      <div style={{ color: COLORS.text, fontWeight: "500" }}>{tx.diagnosis_code}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "2px" }}>Sessions</div>
                      {editingSessionCount?.txId === tx.id ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <input
                            type="number"
                            min="0"
                            value={editingSessionCount.completed}
                            onChange={(e) => setEditingSessionCount(prev => ({ ...prev, completed: e.target.value }))}
                            autoFocus
                            style={{ width: "48px", height: "24px", padding: "0 4px", borderRadius: "4px", border: `1px solid ${COLORS.primary}`, fontSize: "13px", fontWeight: "500", textAlign: "center" }}
                          />
                          <span style={{ color: COLORS.textSecondary }}>/</span>
                          <input
                            type="number"
                            min="0"
                            value={editingSessionCount.planned}
                            onChange={(e) => setEditingSessionCount(prev => ({ ...prev, planned: e.target.value }))}
                            style={{ width: "48px", height: "24px", padding: "0 4px", borderRadius: "4px", border: `1px solid ${COLORS.primary}`, fontSize: "13px", fontWeight: "500", textAlign: "center" }}
                          />
                          <button
                            onClick={() => {
                              const c = parseInt(editingSessionCount.completed);
                              const p = parseInt(editingSessionCount.planned);
                              const updates = {};
                              if (!isNaN(c) && c >= 0) updates.completed_sessions = c;
                              if (!isNaN(p) && p >= 0) updates.planned_sessions = p;
                              if (Object.keys(updates).length) onUpdateTreatment(tx.id, updates);
                              setEditingSessionCount(null);
                            }}
                            style={{ height: "24px", padding: "0 8px", borderRadius: "4px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
                          >✓</button>
                          <button
                            onClick={() => setEditingSessionCount(null)}
                            style={{ height: "24px", padding: "0 8px", borderRadius: "4px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "11px", cursor: "pointer" }}
                          >✕</button>
                        </div>
                      ) : (
                        <div
                          onClick={() => canDo(currentUser, "amend_session_records") && tx.status === "ACTIVE" && setEditingSessionCount({ txId: tx.id, completed: tx.completed_sessions || 0, planned: tx.planned_sessions || 0 })}
                          style={{ color: COLORS.text, fontWeight: "500", cursor: canDo(currentUser, "amend_session_records") && tx.status === "ACTIVE" ? "pointer" : "default", borderBottom: canDo(currentUser, "amend_session_records") && tx.status === "ACTIVE" ? `1px dashed ${COLORS.border}` : "none", display: "inline" }}
                          title={canDo(currentUser, "amend_session_records") && tx.status === "ACTIVE" ? "Click to edit" : ""}
                        >
                          {tx.completed_sessions} / {tx.planned_sessions}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "2px" }}>Assigned</div>
                      <div style={{ color: COLORS.text, fontWeight: "500" }}>{formatDate(tx.created_at)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "2px" }}>Assigned By</div>
                      <div style={{ color: COLORS.text, fontWeight: "500" }}>{getUserName(users, tx.created_by)}</div>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ height: "6px", background: COLORS.borderLight, borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.min(100, (tx.completed_sessions / tx.planned_sessions) * 100)}%`, background: COLORS.accent, borderRadius: "3px", transition: "width 0.3s" }} />
                    </div>
                    <div style={{ fontSize: "11px", color: COLORS.textSecondary, marginTop: "4px" }}>{Math.round((tx.completed_sessions / tx.planned_sessions) * 100)}% complete</div>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginLeft: "16px", flexShrink: 0 }}>
                  <button onClick={canDo(currentUser, "execute_session") ? () => onStartSession(tx) : undefined}
                    style={{ height: "36px", padding: "0 16px", borderRadius: "8px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "13px", fontWeight: "600", cursor: canDo(currentUser, "execute_session") ? "pointer" : "not-allowed", whiteSpace: "nowrap", ...(canDo(currentUser, "execute_session") ? {} : DISABLED_BTN) }}>
                    ▶ Start Session
                  </button>
                  {endingTreatment === tx.id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "10px", background: COLORS.warningLight, borderRadius: "8px", border: `1px solid ${COLORS.warningBorder}` }}>
                      <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.warning }}>End this treatment?</div>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button onClick={() => { onUpdateTreatment(tx.id, { status: "COMPLETED" }); setEndingTreatment(null); }}
                          style={{ height: "30px", padding: "0 10px", borderRadius: "6px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
                          Mark Completed
                        </button>
                        <button onClick={() => {
                            if (!endReason.trim()) return;
                            onUpdateTreatment(tx.id, { status: "ENDED_EARLY", end_reason: endReason });
                            setEndingTreatment(null);
                            setEndReason("");
                          }}
                          style={{ height: "30px", padding: "0 10px", borderRadius: "6px", border: "none", background: COLORS.danger, color: "#FFF", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
                          End Early
                        </button>
                        <button onClick={() => { setEndingTreatment(null); setEndReason(""); }}
                          style={{ height: "30px", padding: "0 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "11px", fontWeight: "500", cursor: "pointer" }}>
                          Cancel
                        </button>
                      </div>
                      <input style={{ height: "30px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px" }}
                        value={endReason} onChange={(e) => setEndReason(e.target.value)} placeholder="Reason for ending early (required if End Early)" />
                    </div>
                  ) : (
                    <button onClick={canDo(currentUser, "end_treatment") ? () => setEndingTreatment(tx.id) : undefined}
                      style={{ height: "30px", padding: "0 12px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "11px", fontWeight: "600", cursor: canDo(currentUser, "end_treatment") ? "pointer" : "not-allowed", ...(canDo(currentUser, "end_treatment") ? {} : DISABLED_BTN) }}>
                      End Treatment
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Next Visits — Google Calendar integration */}
      {calConnected && (
        <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, margin: 0 }}>
              Next Visits
              <span style={{ color: COLORS.textSecondary, fontWeight: "400", fontSize: "13px", marginLeft: "8px" }}>({upcomingVisits.length})</span>
            </h3>
            <div style={{ display: "flex", gap: "8px" }}>
              {selectedVisits.length > 0 && (
                <button onClick={handleDeleteSelectedVisits}
                  disabled={deletingVisits}
                  style={{ height: "36px", padding: "0 14px", borderRadius: "8px", border: `1px solid ${COLORS.dangerBorder}`, background: COLORS.dangerLight, color: COLORS.danger, fontSize: "12px", fontWeight: "600", cursor: deletingVisits ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                  {deletingVisits ? "Deleting..." : `Delete (${selectedVisits.length})`}
                </button>
              )}
              <button onClick={() => setShowSchedule(true)}
                style={{ height: "36px", padding: "0 16px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                📅 Schedule Visit
              </button>
            </div>
          </div>
          {upcomingVisits.length === 0 ? (
            <div style={{ fontSize: "13px", color: COLORS.textSecondary, padding: "8px 0" }}>
              No upcoming visits scheduled.
            </div>
          ) : (
            <>
              {/* Select all */}
              {upcomingVisits.length > 1 && (
                <div style={{ marginBottom: "6px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: COLORS.textSecondary, cursor: "pointer" }}>
                    <input type="checkbox"
                      checked={selectedVisits.length === upcomingVisits.filter(ev => ev.id).length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedVisits(upcomingVisits.filter(ev => ev.id).map(ev => ev.id));
                        } else {
                          setSelectedVisits([]);
                        }
                      }}
                      style={{ width: "14px", height: "14px" }} />
                    Select all
                  </label>
                </div>
              )}
              {upcomingVisits.map((ev, i) => {
                const startDt = ev.start ? new Date(ev.start) : null;
                const isPast = startDt && startDt < new Date();
                const isSelected = ev.id && selectedVisits.includes(ev.id);
                return (
                  <div key={ev.id || i} style={{
                    padding: "10px 14px",
                    borderBottom: i < upcomingVisits.length - 1 ? `1px solid ${COLORS.borderLight}` : "none",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    opacity: isPast ? 0.5 : 1,
                    background: isSelected ? "rgba(231,76,60,0.05)" : "transparent",
                    borderRadius: "6px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {ev.id && (
                        <input type="checkbox" checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedVisits((prev) => [...prev, ev.id]);
                            } else {
                              setSelectedVisits((prev) => prev.filter((id) => id !== ev.id));
                            }
                          }}
                          style={{ width: "14px", height: "14px" }} />
                      )}
                      <div style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text, minWidth: "90px" }}>
                        {startDt ? startDt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : ""}
                      </div>
                      <div style={{ fontSize: "13px", color: COLORS.text }}>
                        {startDt ? startDt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {isPast && <span style={{ fontSize: "11px", color: COLORS.textSecondary }}>Done</span>}
                      {ev.id && !isPast && (
                        <button onClick={async () => {
                            try {
                              await api.calendarDeleteEvent(ev.id);
                              setUpcomingVisits((prev) => prev.filter((v) => v.id !== ev.id));
                              setSelectedVisits((prev) => prev.filter((id) => id !== ev.id));
                            } catch (err) { console.error("Delete event:", err); }
                          }}
                          style={{ padding: "3px 8px", borderRadius: "4px", border: `1px solid ${COLORS.dangerBorder}`, background: "transparent", color: COLORS.danger, fontSize: "10px", fontWeight: "600", cursor: "pointer" }}>
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Schedule Visit Modal */}
      {showSchedule && (
        <ScheduleVisitModal
          patient={patient}
          onClose={() => setShowSchedule(false)}
          onCreated={(ev) => {
            setUpcomingVisits((prev) => [...prev, ev].sort((a, b) => new Date(a.start) - new Date(b.start)));
          }}
        />
      )}


      {/* ─── Assessments Section ─────────────────────────────── */}
      <div ref={assessmentSectionRef} style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, margin: 0 }}>
              Assessments
              <span style={{ color: COLORS.textSecondary, fontWeight: "400", fontSize: "13px", marginLeft: "8px" }}>
                ({(assessments || []).length})
              </span>
            </h3>
            {/* Tabs */}
            <div style={{ display: "flex", gap: "4px", background: COLORS.inputBg, borderRadius: "8px", padding: "3px" }}>
              {[["scores", "Scores"], ["trends", "Trends"]].map(([key, label]) => (
                <button key={key} onClick={() => setAssessmentTab(key)}
                  style={{ padding: "4px 14px", borderRadius: "6px", border: "none", fontSize: "12px", fontWeight: "600", cursor: "pointer",
                    background: assessmentTab === key ? COLORS.surface : "transparent",
                    color: assessmentTab === key ? COLORS.primary : COLORS.textSecondary,
                    boxShadow: assessmentTab === key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  }}>{label}</button>
              ))}
            </div>
          </div>
          {canDo(currentUser, "input_outcome_scores") && activeTx.length > 0 && (
            <div style={{ display: "flex", gap: "6px" }}>
              <button onClick={async () => { setRefreshingAssessments(true); await onRefreshAssessments(); setRefreshingAssessments(false); }}
                style={{ height: "32px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ animation: refreshingAssessments ? "spin 1s linear infinite" : "none" }}>
                  <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
                {refreshingAssessments ? "" : "Refresh"}
              </button>
              <button onClick={() => setShowAssessmentForm(showAssessmentForm ? null : { treatmentId: activeTx[0]?.id, scaleId: null })}
                style={{ height: "32px", padding: "0 14px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                <IconPlus /> Administer
              </button>
            </div>
          )}
        </div>

        {/* Assessment Entry Form */}
        {showAssessmentForm && (
          <AssessmentEntryForm
            scales={assessmentScales || []}
            treatments={activeTx}
            initialTreatmentId={showAssessmentForm.treatmentId}
            initialScaleId={showAssessmentForm.scaleId}
            patient={patient}
            onSubmit={async (data) => {
              await onSubmitAssessment(data);
              setShowAssessmentForm(null);
            }}
            onGenerateLink={async (data) => {
              const result = await onGenerateLink(data);
              if (result) setShowQR({ link: result.link, scaleName: (assessmentScales || []).find(s => s.id === data.scale_id)?.short_name || "Assessment" });
              setShowAssessmentForm(null);
            }}
            onCancel={() => setShowAssessmentForm(null)}
          />
        )}

        {/* QR Code Display */}
        {showQR && (
          <div style={{ padding: "20px", margin: "0 0 16px", background: COLORS.inputBg, borderRadius: "10px", border: `1px solid ${COLORS.borderLight}`, textAlign: "center" }}>
            <div style={{ fontSize: "14px", fontWeight: "600", color: COLORS.text, marginBottom: "12px" }}>{showQR.scaleName} — Patient Self-Administration</div>
            <div style={{ marginBottom: "12px" }}>
              {/* QR code as a data URL using a simple SVG-based approach */}
              <div style={{ display: "inline-block", padding: "16px", background: "#FFF", borderRadius: "12px", border: `2px solid ${COLORS.border}` }}>
                <div style={{ width: "200px", height: "200px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9f9f9", borderRadius: "8px", fontSize: "12px", color: COLORS.textSecondary }}>
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(showQR.link)}`} alt="QR Code" style={{ width: "200px", height: "200px" }} onError={(e) => { e.target.style.display = "none"; e.target.parentNode.innerHTML = '<div style="padding:20px;font-size:11px;color:#888">QR code unavailable offline.<br/>Use the link below.</div>'; }} />
                </div>
              </div>
            </div>
            <div style={{ fontSize: "11px", color: COLORS.textSecondary, marginBottom: "8px" }}>Or share this link:</div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
              <input readOnly value={showQR.link} style={{ width: "320px", height: "32px", padding: "0 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "11px", color: COLORS.text, background: "#FFF" }}
                onClick={(e) => e.target.select()} />
              <button onClick={() => { navigator.clipboard?.writeText(showQR.link); }}
                style={{ height: "32px", padding: "0 12px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.primary, fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>Copy</button>
            </div>
            <button onClick={() => setShowQR(null)}
              style={{ marginTop: "12px", height: "30px", padding: "0 16px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "12px", fontWeight: "500", cursor: "pointer" }}>Close</button>
          </div>
        )}

        {/* Scores Tab */}
        {assessmentTab === "scores" && (
          <div>
            {(assessments || []).length === 0 ? (
              <div style={{ padding: "32px", textAlign: "center", color: COLORS.textSecondary, fontSize: "13px" }}>
                No assessments recorded. Use "Administer" to enter scores or send a self-report link.
              </div>
            ) : (
              (() => {
                // Group by treatment
                const grouped = {};
                (assessments || []).forEach((a) => {
                  const txId = a.treatment_assignment_id || "unlinked";
                  if (!grouped[txId]) grouped[txId] = [];
                  grouped[txId].push(a);
                });
                // Order: active treatments first
                const txOrder = [...activeTx.map(t => t.id), ...pastTx.map(t => t.id)];
                const sortedKeys = Object.keys(grouped).sort((a, b) => {
                  const ai = txOrder.indexOf(a);
                  const bi = txOrder.indexOf(b);
                  return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                });

                return sortedKeys.map((txId) => {
                  const txAssessments = grouped[txId].sort((a, b) => new Date(b.administered_at) - new Date(a.administered_at));
                  const tx = treatments.find(t => t.id === txId);
                  const isActive = tx?.status === "ACTIVE";
                  const isExpanded = isActive || !!expandedAssessmentTx[txId];

                  return (
                    <div key={txId} style={{ marginBottom: "16px" }}>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: isExpanded ? "8px" : "0", cursor: isActive ? "default" : "pointer", userSelect: "none" }}
                        onClick={() => { if (!isActive) setExpandedAssessmentTx(prev => ({ ...prev, [txId]: !prev[txId] })); }}
                      >
                        {!isActive && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}>
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        )}
                        <span style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text }}>{tx?.protocol_name || "Unlinked"}</span>
                        {tx && <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "600",
                          background: isActive ? "#E8F8F0" : tx.status === "COMPLETED" ? "#F0F2F5" : COLORS.dangerLight,
                          color: isActive ? COLORS.accent : tx.status === "COMPLETED" ? COLORS.textSecondary : COLORS.danger,
                        }}>{isActive ? "Active" : tx.status === "COMPLETED" ? "Completed" : "Ended Early"}</span>}
                        <span style={{ fontSize: "12px", color: COLORS.textSecondary }}>{txAssessments.length} score{txAssessments.length !== 1 ? "s" : ""}</span>
                      </div>
                      {isExpanded && txAssessments.map((a) => {
                        const aHasAmendments = a.amendments && a.amendments.length > 0;
                        const aIsVoided = a.is_voided;
                        const aBorderColor = aIsVoided ? COLORS.danger : aHasAmendments ? "#F0E68C" : COLORS.borderLight;
                        const aBg = aIsVoided ? "#FDF2F2" : aHasAmendments ? "#FFFDF0" : COLORS.inputBg;
                        return (
                        <div key={a.id} style={{ padding: "10px 14px", border: `1px solid ${aBorderColor}`, borderRadius: "8px", marginBottom: "6px", background: aBg, cursor: "pointer", opacity: aIsVoided ? 0.6 : 1 }}
                          onClick={() => setExpandedAssessments(prev => ({ ...prev, [a.id]: !prev[a.id] }))}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                              <span style={{ fontSize: "13px", fontWeight: "600", color: COLORS.primary, textDecoration: aIsVoided ? "line-through" : "none" }}>{a.scale_short_name}</span>
                              <span style={{ fontSize: "22px", fontWeight: "700", color: COLORS.text, textDecoration: aIsVoided ? "line-through" : "none" }}>{a.total_score}</span>
                              {a.severity_label && (
                                <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "700",
                                  background: a.severity_label === "Minimal" || a.severity_label === "Normal" ? "#E8F8F0" :
                                    a.severity_label === "Mild" ? "#FFF9E6" :
                                    a.severity_label === "Moderate" ? "#FFF3E6" : "#FDEDEC",
                                  color: a.severity_label === "Minimal" || a.severity_label === "Normal" ? COLORS.accent :
                                    a.severity_label === "Mild" ? "#8B7000" :
                                    a.severity_label === "Moderate" ? "#D4700A" : COLORS.danger,
                                }}>{a.severity_label}</span>
                              )}
                              {a.is_baseline && <span style={{ padding: "2px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: "700", background: COLORS.primaryLight, color: COLORS.primary, letterSpacing: "0.3px" }}>BASELINE</span>}
                              {a.meets_response && <span style={{ padding: "2px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: "700", background: "#E8F8F0", color: COLORS.accent, letterSpacing: "0.3px" }}>RESPONSE</span>}
                              {a.meets_remission && <span style={{ padding: "2px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: "700", background: "#D5F5E3", color: "#1E8449", letterSpacing: "0.3px" }}>REMISSION</span>}
                              {aHasAmendments && <span style={{ padding: "2px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: "700", background: "#FFF3E6", color: "#D4700A", letterSpacing: "0.3px" }}>AMENDED ({a.amendments.length}×)</span>}
                              {aIsVoided && <span style={{ padding: "2px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: "700", background: COLORS.dangerLight, color: COLORS.danger, letterSpacing: "0.3px" }}>VOIDED</span>}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              {a.percent_change_from_baseline != null && (
                                <span style={{ fontSize: "12px", fontWeight: "600", color: a.percent_change_from_baseline <= -30 ? COLORS.accent : a.percent_change_from_baseline >= 0 ? COLORS.danger : COLORS.warning }}>
                                  {a.percent_change_from_baseline > 0 ? "+" : ""}{a.percent_change_from_baseline}%
                                </span>
                              )}
                              <span style={{ fontSize: "11px", color: COLORS.textSecondary }}>{formatDate(a.administered_at)}</span>
                              <span style={{ fontSize: "10px", color: COLORS.textSecondary, textTransform: "uppercase" }}>{a.administration_mode === "SELF_REPORT" ? "Self" : a.administration_mode === "CLINICIAN" ? "Clinician" : "Manual"}</span>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                style={{ transform: expandedAssessments[a.id] ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            </div>
                          </div>
                          {expandedAssessments[a.id] && (
                            <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: `1px solid ${COLORS.borderLight}` }}>
                              {a.responses && a.responses.length > 0 && a.responses.map((r, idx) => {
                                const scale = (assessmentScales || []).find(s => s.id === a.scale_id);
                                const question = scale?.questions?.find(q => q.id === r.question_id);
                                return (
                                  <div key={r.id || idx} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "12px" }}>
                                    <span style={{ color: COLORS.textSecondary, flex: 1 }}>
                                      {question ? `${question.question_number}. ${question.question_text.substring(0, 80)}${question.question_text.length > 80 ? "..." : ""}` : `Q${idx + 1}`}
                                    </span>
                                    <span style={{ fontWeight: "600", color: COLORS.text, marginLeft: "12px", minWidth: "20px", textAlign: "right" }}>{r.response_value}</span>
                                  </div>
                                );
                              })}
                              {a.notes && <div style={{ marginTop: "6px", fontSize: "11px", color: COLORS.textSecondary, fontStyle: "italic" }}>Note: {a.notes}</div>}

                              {/* Amendment history */}
                              {aHasAmendments && (
                                <div style={{ marginTop: "8px", padding: "8px 10px", background: "#FFFBE6", borderRadius: "6px", border: "1px solid #F0E68C" }}>
                                  <div style={{ fontSize: "10px", fontWeight: "700", color: "#8B7000", textTransform: "uppercase", marginBottom: "4px" }}>Amendment History</div>
                                  {a.amendments.map((am) => (
                                    <div key={am.id} style={{ fontSize: "11px", color: COLORS.text, marginBottom: "4px" }}>
                                      <span style={{ fontWeight: "600" }}>{am.field_changed}</span>: {am.old_value} → {am.new_value}
                                      <span style={{ color: COLORS.textSecondary }}> — {getUserName(users, am.amended_by)}, {formatDate(am.amended_at)}</span>
                                      <div style={{ fontSize: "10px", fontStyle: "italic", color: "#8B7000" }}>"{am.reason}"</div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Void info */}
                              {aIsVoided && (
                                <div style={{ marginTop: "8px", padding: "8px 10px", background: "#FDEDEC", borderRadius: "6px", border: "1px solid #F5B7B1" }}>
                                  <div style={{ fontSize: "10px", fontWeight: "700", color: COLORS.danger, textTransform: "uppercase", marginBottom: "2px" }}>Voided</div>
                                  <div style={{ fontSize: "11px", color: COLORS.text }}>Reason: {a.void_reason}</div>
                                  <div style={{ fontSize: "10px", color: COLORS.textSecondary }}>by {getUserName(users, a.voided_by)} on {formatDate(a.voided_at)}</div>
                                </div>
                              )}

                              {/* Amend buttons */}
                              {canDo(currentUser, "amend_session_records") && !aIsVoided && (
                                <div style={{ marginTop: "8px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                  <button onClick={(e) => { e.stopPropagation(); setAmendModal({ type: "assessment", record: a, field: "total_score", fieldLabel: "Total Score", currentValue: a.total_score }); }}
                                    style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "4px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer" }}>
                                    Amend Score
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setAmendModal({ type: "assessment", record: a, field: "notes", fieldLabel: "Notes", currentValue: a.notes || "" }); }}
                                    style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "4px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer" }}>
                                    Amend Notes
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setAmendModal({ type: "assessment", record: a, field: "administered_at", fieldLabel: "Date Administered", currentValue: a.administered_at || "" }); }}
                                    style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "4px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer" }}>
                                    Amend Date
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setVoidConfirm({ type: "assessment", record: a }); }}
                                    style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "4px", border: `1px solid ${COLORS.danger}`, background: "#FFF5F5", color: COLORS.danger, cursor: "pointer", fontWeight: "600" }}>
                                    Void
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );})}

                    </div>
                  );
                });
              })()
            )}
          </div>
        )}

        {/* Trends Tab */}
        {assessmentTab === "trends" && (
          <div>
            {/* Treatment selector for trends */}
            {treatments.length > 0 && (() => {
              const allTx = [...activeTx, ...pastTx];
              // Resolve effective selection: null means active treatments
              const effectiveIds = trendTreatmentIds || new Set(activeTx.map(tx => tx.id));
              const toggleTx = (txId) => {
                const current = trendTreatmentIds || new Set(activeTx.map(tx => tx.id));
                const next = new Set(current);
                if (next.has(txId)) {
                  next.delete(txId);
                } else {
                  next.add(txId);
                }
                setTrendTreatmentIds(next);
              };
              const allSelected = allTx.every(tx => effectiveIds.has(tx.id));
              return (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase" }}>Showing:</span>
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {allTx.length > 1 && (
                      <button onClick={() => {
                        if (allSelected) {
                          setTrendTreatmentIds(new Set(activeTx.map(tx => tx.id)));
                        } else {
                          setTrendTreatmentIds(new Set(allTx.map(tx => tx.id)));
                        }
                      }}
                        style={{ padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", cursor: "pointer",
                          border: `1px solid ${allSelected ? COLORS.primary : COLORS.border}`,
                          background: allSelected ? COLORS.primaryLight : COLORS.surface,
                          color: allSelected ? COLORS.primary : COLORS.textSecondary,
                        }}>All</button>
                    )}
                    {allTx.map(tx => {
                      const isSelected = effectiveIds.has(tx.id);
                      return (
                        <button key={tx.id} onClick={() => toggleTx(tx.id)}
                          style={{ padding: "4px 12px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", cursor: "pointer",
                            border: `1px solid ${isSelected ? COLORS.primary : COLORS.border}`,
                            background: isSelected ? COLORS.primaryLight : COLORS.surface,
                            color: isSelected ? COLORS.primary : COLORS.textSecondary,
                          }}>
                          {tx.protocol_name}
                          <span style={{ marginLeft: "4px", fontSize: "9px", opacity: 0.7 }}>
                            {tx.status === "ACTIVE" ? "" : tx.status === "COMPLETED" ? "(done)" : "(ended)"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            <AssessmentTrendGraphs
              assessments={(assessments || []).filter(a => {
                if (a.is_voided) return false; // Exclude voided assessments from trends
                const effectiveIds = trendTreatmentIds || new Set(activeTx.map(tx => tx.id));
                return effectiveIds.has(a.treatment_assignment_id);
              })}
              assessmentScales={assessmentScales || []}
              treatments={treatments}
            />
          </div>
        )}
      </div>

      {/* Session History — grouped by treatment */}
      <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, margin: 0 }}>
            Session History / Past Treatments
          </h3>
          {canDo(currentUser, "execute_session") && treatments.filter(t => t.status === "ACTIVE").length > 0 && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                onClick={async () => { if (onRefreshAssessments) { await onRefreshAssessments(); } }}
                style={{ padding: "6px 12px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}
              >
                ↻ Refresh
              </button>
              <button
                onClick={() => {
                  if (showManualSession) { setShowManualSession(null); return; }
                  const activeTxList = treatments.filter(t => t.status === "ACTIVE");
                  setShowManualSession(activeTxList[0]?.id || null);
                  setManualSessionForm({ session_date: localDateStr(new Date()), session_notes: "" });
                }}
                style={{ padding: "6px 14px", borderRadius: "8px", border: `1px solid ${showManualSession ? COLORS.danger : COLORS.primary}`, background: showManualSession ? "#FFF5F5" : COLORS.primaryLight, color: showManualSession ? COLORS.danger : COLORS.primary, fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}
              >
                {showManualSession ? "✕ Cancel" : "＋ Add Manual Session"}
              </button>
            </div>
          )}
        </div>

        {/* Manual Session Entry Form */}
        {showManualSession && (() => {
          const activeTxList = treatments.filter(t => t.status === "ACTIVE");
          const selectedTx = treatments.find(t => t.id === showManualSession);
          return (
            <div style={{ background: "#F5F3FF", border: "1px solid #C4B5FD", borderRadius: "10px", padding: "16px", marginBottom: "16px" }}>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#6366F1", marginBottom: "12px" }}>Manual Session Entry</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "12px" }}>
                {activeTxList.length > 1 && (
                  <div>
                    <label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "3px" }}>Treatment</label>
                    <select value={showManualSession} onChange={e => setShowManualSession(e.target.value)}
                      style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", minWidth: "180px" }}>
                      {activeTxList.map(tx => <option key={tx.id} value={tx.id}>{tx.protocol_name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "3px" }}>Date</label>
                  <input type="date" value={manualSessionForm.session_date || ""} onChange={e => setManualSessionForm(f => ({ ...f, session_date: e.target.value }))}
                    style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px" }} />
                </div>
                <div>
                  <label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "3px" }}>Session #</label>
                  <input type="number" placeholder="Auto" value={manualSessionForm.session_number || ""} onChange={e => setManualSessionForm(f => ({ ...f, session_number: e.target.value }))}
                    style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", width: "70px" }} />
                </div>
                <div>
                  <label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "3px" }}>MT %</label>
                  <input type="number" placeholder="e.g. 48" value={manualSessionForm.motor_threshold_value || ""} onChange={e => setManualSessionForm(f => ({ ...f, motor_threshold_value: e.target.value }))}
                    style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", width: "70px" }} />
                </div>
                <div>
                  <label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "3px" }}>Final Intensity %</label>
                  <input type="number" placeholder="e.g. 58" value={manualSessionForm.final_intensity_pct || ""} onChange={e => setManualSessionForm(f => ({ ...f, final_intensity_pct: e.target.value }))}
                    style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", width: "70px" }} />
                </div>
                <div>
                  <label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "3px" }}>Delivered Pulses</label>
                  <input type="number" placeholder={selectedTx ? String(selectedTx.planned_sessions || "") : ""} value={manualSessionForm.delivered_total_pulses || ""} onChange={e => setManualSessionForm(f => ({ ...f, delivered_total_pulses: e.target.value }))}
                    style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", width: "100px" }} />
                </div>
                <div>
                  <label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "3px" }}>CPT Code</label>
                  <select value={manualSessionForm.cpt_code || ""} onChange={e => setManualSessionForm(f => ({ ...f, cpt_code: e.target.value }))}
                    style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px" }}>
                    <option value="">Auto</option>
                    <option value="90867">90867</option>
                    <option value="90868">90868</option>
                    <option value="90869">90869</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "3px" }}>Notes (optional)</label>
                <input type="text" placeholder="e.g. Historical entry from paper records" value={manualSessionForm.session_notes || ""} onChange={e => setManualSessionForm(f => ({ ...f, session_notes: e.target.value }))}
                  style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", width: "100%", boxSizing: "border-box" }} />
              </div>
              <button
                disabled={savingManual}
                onClick={async () => {
                  setSavingManual(true);
                  try {
                    await api.createManualSession({
                      treatment_assignment_id: showManualSession,
                      ...manualSessionForm,
                    });
                    setShowManualSession(null);
                    setManualSessionForm({});
                    // Trigger parent data refresh
                    if (onRefreshAssessments) await onRefreshAssessments();
                  } catch (e) {
                    alert(e?.error || e?.message || "Failed to save manual session");
                  }
                  setSavingManual(false);
                }}
                style={{ padding: "8px 20px", borderRadius: "8px", border: "none", background: "#6366F1", color: "#FFF", fontSize: "12px", fontWeight: "600", cursor: savingManual ? "wait" : "pointer", opacity: savingManual ? 0.7 : 1 }}
              >
                {savingManual ? "Saving..." : "Save Manual Session"}
              </button>
            </div>
          );
        })()}
        {(sessions || []).filter((s) => s.patient_id === patient.id).length === 0 ? (
          <div style={{ padding: "32px", textAlign: "center", color: COLORS.textSecondary }}>
            <p style={{ fontSize: "14px" }}>No sessions recorded yet. Start a session from an active treatment above.</p>
          </div>
        ) : (
          (() => {
            const patientSessions = (sessions || []).filter((s) => s.patient_id === patient.id);
            const activeTx = treatments.filter((t) => t.status === "ACTIVE");
            const inactiveTx = treatments.filter((t) => t.status !== "ACTIVE");
            const orderedTx = [...activeTx, ...inactiveTx];
            // Also gather orphan sessions not tied to any current treatment
            const txIds = new Set(orderedTx.map((t) => t.id));
            const orphanSessions = patientSessions.filter((s) => !txIds.has(s.treatment_assignment_id));

            const canAmend = canDo(currentUser, "amend_session_records");

            const sessionFieldMap = [
              { field: "session_date", label: "Session Date" },
              { field: "session_number", label: "Session #" },
              { field: "motor_threshold_value", label: "Motor Threshold" },
              { field: "final_intensity_pct", label: "Final Intensity %" },
              { field: "delivered_total_pulses", label: "Delivered Pulses" },
              { field: "cpt_code", label: "CPT Code" },
              { field: "session_notes", label: "Session Notes" },
            ];

            const renderSessionTable = (txSessions) => (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["#", "Date", "CPT", "MT", "Intensity", "Prescribed", "Delivered", "Operator", "Status", ...(canAmend ? [""] : [])].map((h) => (
                        <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: `1px solid ${COLORS.border}`, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {txSessions.sort((a, b) => new Date(b.session_date) - new Date(a.session_date)).map((s) => {
                      const hasAmendments = s.amendments && s.amendments.length > 0;
                      const isVoided = s.is_voided;
                      const rowBg = isVoided ? "#F9F0F0" : hasAmendments ? "#FFFDF0" : "transparent";
                      const dimStyle = isVoided ? { opacity: 0.5, textDecoration: "line-through" } : {};
                      return (
                      <tr key={s.id} style={{ borderBottom: `1px solid ${COLORS.borderLight}`, background: rowBg }}>
                        <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.textSecondary, ...dimStyle }}>{s.session_number}</td>
                        <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.text, whiteSpace: "nowrap", ...dimStyle }}>{formatDate(s.session_date)}</td>
                        <td style={{ padding: "8px 10px", ...dimStyle }}><span style={{ padding: "2px 6px", borderRadius: "4px", fontSize: "10px", fontWeight: "600", background: COLORS.primaryLight, color: COLORS.primary }}>{s.cpt_code}</span></td>
                        <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.textSecondary, ...dimStyle }}>{s.motor_threshold_value}%</td>
                        <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.textSecondary, ...dimStyle }}>{s.final_intensity_pct}%</td>
                        <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.textSecondary, ...dimStyle }}>{s.prescribed_total_pulses?.toLocaleString()}</td>
                        <td style={{ padding: "8px 10px", fontSize: "12px", color: s.delivered_total_pulses < s.prescribed_total_pulses ? COLORS.warning : COLORS.text, fontWeight: "500", ...dimStyle }}>{s.delivered_total_pulses?.toLocaleString()}</td>
                        <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.textSecondary, ...dimStyle }}>{getUserName(users, s.operator_id)}</td>
                        <td style={{ padding: "8px 10px" }}>
                          {isVoided ? (
                            <div>
                              <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "600", background: COLORS.dangerLight, color: COLORS.danger }}>VOIDED</span>
                              {s.void_reason && <div style={{ fontSize: "10px", color: COLORS.danger, marginTop: "2px" }}>Reason: {s.void_reason}</div>}
                              <div style={{ fontSize: "9px", color: COLORS.textSecondary, marginTop: "1px" }}>
                                by {getUserName(users, s.voided_by)} on {formatDate(s.voided_at)}
                              </div>
                            </div>
                          ) : (
                            <>
                              <span style={{
                                padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "600",
                                background: s.status === "COMPLETED" ? "#E8F8F0" : COLORS.warningLight,
                                color: s.status === "COMPLETED" ? COLORS.accent : COLORS.warning,
                              }}>
                                {s.status === "COMPLETED" ? "Completed" : "Interrupted"}
                              </span>
                              {s.session_notes && <div style={{ fontSize: "10px", color: COLORS.textSecondary, marginTop: "2px" }}>Note: {s.session_notes}</div>}
                              {s.is_manual_entry && (
                                <div style={{ fontSize: "9px", color: "#6366F1", fontWeight: "600", marginTop: "2px" }}>
                                  MANUAL ENTRY
                                </div>
                              )}
                              {hasAmendments && (
                                <div style={{ fontSize: "9px", color: "#D4700A", fontWeight: "600", marginTop: "2px" }}>
                                  AMENDED ({s.amendments.length}×)
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        {canAmend && (
                          <td style={{ padding: "8px 6px", whiteSpace: "nowrap" }}>
                            {!isVoided && (
                              <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                <select
                                  value=""
                                  onChange={(e) => {
                                    const f = sessionFieldMap.find(sf => sf.field === e.target.value);
                                    if (f) setAmendModal({ type: "session", record: s, field: f.field, fieldLabel: f.label, currentValue: s[f.field] });
                                  }}
                                  style={{ fontSize: "10px", padding: "3px 6px", borderRadius: "4px", border: `1px solid ${COLORS.border}`, cursor: "pointer", color: COLORS.textSecondary, background: COLORS.surface, appearance: "auto" }}
                                >
                                  <option value="">Amend...</option>
                                  {sessionFieldMap.map(f => (
                                    <option key={f.field} value={f.field}>{f.label}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => setVoidConfirm({ type: "session", record: s })}
                                  title="Void this session"
                                  style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "4px", border: `1px solid ${COLORS.danger}`, background: "#FFF5F5", color: COLORS.danger, cursor: "pointer", fontWeight: "600" }}
                                >
                                  Void
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            );

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {orderedTx.map((tx) => {
                  const txSessions = patientSessions.filter((s) => s.treatment_assignment_id === tx.id);
                  if (txSessions.length === 0) return null;
                  const isActive = tx.status === "ACTIVE";
                  const isExpanded = isActive || !!expandedHistoryTx[tx.id];
                  return (
                    <div key={tx.id}>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: isExpanded ? "8px" : "0", cursor: isActive ? "default" : "pointer", userSelect: "none" }}
                        onClick={() => { if (!isActive) setExpandedHistoryTx((prev) => ({ ...prev, [tx.id]: !prev[tx.id] })); }}
                      >
                        {!isActive && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}>
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        )}
                        <span style={{ fontSize: "14px", fontWeight: "600", color: COLORS.text }}>{tx.protocol_name}</span>
                        <span style={{
                          padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "600",
                          background: isActive ? "#E8F8F0" : tx.status === "COMPLETED" ? "#F0F2F5" : COLORS.dangerLight,
                          color: isActive ? COLORS.accent : tx.status === "COMPLETED" ? COLORS.textSecondary : COLORS.danger,
                        }}>
                          {isActive ? "Active" : tx.status === "COMPLETED" ? "Completed" : "Ended Early"}
                        </span>
                      </div>
                      {isExpanded && renderSessionTable(txSessions)}
                    </div>
                  );
                })}
                {orphanSessions.length > 0 && (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                      <span style={{ fontSize: "14px", fontWeight: "600", color: COLORS.textSecondary }}>Other Sessions</span>
                    </div>
                    {renderSessionTable(orphanSessions)}
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* ─── Reports & Export Section ─────────────────────────── */}
      {canDo(currentUser, "generate_reports") && (
        <ReportsSection
          patient={patient}
          treatments={treatments}
          currentUser={currentUser}
          providers={providers}
        />
      )}

      {/* ─── Amendment Modal ──────────────────────────────────── */}
      {amendModal && (
        <AmendmentModal
          type={amendModal.type}
          record={amendModal.record}
          field={amendModal.field}
          fieldLabel={amendModal.fieldLabel}
          currentValue={amendModal.currentValue}
          users={users}
          onClose={() => setAmendModal(null)}
          onSubmit={async (data) => {
            if (amendModal.type === "session") {
              await onAmendSession(amendModal.record.id, data);
            } else {
              await onAmendAssessment(amendModal.record.id, data);
            }
          }}
        />
      )}

      {/* ─── Void Confirmation Modal ──────────────────────────── */}
      {voidConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => { setVoidConfirm(null); setVoidReason(""); }}>
          <div style={{ background: COLORS.surface, borderRadius: "12px", padding: "28px", maxWidth: "480px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: "16px", fontWeight: "700", color: COLORS.danger, marginBottom: "8px" }}>
              Void {voidConfirm.type === "session" ? "Session" : "Assessment"}?
            </h3>
            <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "4px" }}>
              This action will mark the record as voided. It will remain in the audit trail but will no longer count toward treatment progress.
            </p>
            {voidConfirm.type === "session" && (
              <p style={{ fontSize: "12px", color: COLORS.text, marginBottom: "12px" }}>
                Session #{voidConfirm.record.session_number} — {formatDate(voidConfirm.record.session_date)} — {voidConfirm.record.cpt_code}
              </p>
            )}
            {voidConfirm.type === "assessment" && (
              <p style={{ fontSize: "12px", color: COLORS.text, marginBottom: "12px" }}>
                {voidConfirm.record.scale_short_name} — Score: {voidConfirm.record.total_score} — {formatDate(voidConfirm.record.administered_at)}
              </p>
            )}
            <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.text, display: "block", marginBottom: "4px" }}>
              Reason for voiding <span style={{ color: COLORS.danger }}>*</span>
            </label>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g., Wrong patient chart — session performed on Patient B, not this patient"
              rows={3}
              style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
              <button
                onClick={() => { setVoidConfirm(null); setVoidReason(""); }}
                style={{ padding: "8px 16px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer", fontSize: "13px" }}
              >
                Cancel
              </button>
              <button
                disabled={voidReason.trim().length < 3 || voiding}
                onClick={async () => {
                  setVoiding(true);
                  try {
                    if (voidConfirm.type === "session") {
                      await onVoidSession(voidConfirm.record.id, { reason: voidReason.trim() });
                    } else {
                      await onVoidAssessment(voidConfirm.record.id, { reason: voidReason.trim() });
                    }
                    setVoidConfirm(null);
                    setVoidReason("");
                  } catch (e) { console.error("Void failed:", e); }
                  setVoiding(false);
                }}
                style={{
                  padding: "8px 16px", borderRadius: "6px", border: "none", fontWeight: "600", fontSize: "13px", cursor: voidReason.trim().length < 3 || voiding ? "not-allowed" : "pointer",
                  background: voidReason.trim().length < 3 || voiding ? "#F0F2F5" : COLORS.danger,
                  color: voidReason.trim().length < 3 || voiding ? COLORS.textSecondary : "#FFF",
                }}
              >
                {voiding ? "Voiding..." : "Void Record"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reports & Export Section ─────────────────────────────────

